// ============================================================
// api/client.js
// Fetch wrapper for the admin SWA's calls to the mgmt/* API
// (via APIM). Single source of truth for HTTP — every endpoint
// module in this directory imports from here.
//
// Auth posture: JWT (Entra access token) acquired via MSAL and
// attached as `Authorization: Bearer <token>` on every request.
// The MSAL PublicClientApplication is registered at app bootstrap
// (index.js) via setMsalInstance(...) — apiGet/apiPost will
// throw a clear error if a call is made before bootstrap runs.
//
// Token acquisition flow:
//   1. acquireTokenSilent({ ...apiRequest, account }) — fast,
//      cached path. Returns a fresh access token from MSAL's
//      session cache or transparently refreshes via the refresh
//      token if expired.
//   2. On InteractionRequiredAuthError (consent_required /
//      interaction_required / login_required), fall through to
//      acquireTokenPopup. Rare in steady state; happens on first
//      use after sign-in if the API scope wasn't consented to yet,
//      or after admin policy changes.
//
// 401 retry: on any 401 response from a mgmt/* call, force MSAL
// to drop its cached token (forceRefresh) and retry once. Covers
// edge cases like a revoked refresh token, server-side scope
// changes, or token rotation mid-session. A second 401 surfaces
// as an ApiError to the caller — the UI handles it (usually a
// forced sign-out).
//
// Backend reference:
//   backend/func-carehub-api/src/shared/withAuth.js
//   docs/design/jwt-auth-design.md §§4–6
//
// Fail-loud at module load if REACT_APP_API_BASE_URL is missing —
// silent fallback would surface as confusing 404s/0-status network
// errors at runtime.
//
// No timeouts. No retry logic beyond the 401 case. Add to the
// backlog if needed.
// ============================================================

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

const RAW_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!RAW_BASE_URL) {
  throw new Error(
    'REACT_APP_API_BASE_URL is not set. The admin SWA build requires this env var to be injected at build time (azure-pipelines.yml stage 8).'
  );
}
const API_BASE_URL = RAW_BASE_URL.replace(/\/+$/, '');

// ── MSAL instance reference ──────────────────────────────────
// Registered at bootstrap (index.js) before <App /> mounts. All
// API calls happen inside <MsalProvider>, so by the time apiGet /
// apiPost run, the instance is always set. The defensive throw
// catches the wrong bootstrap order during refactors.

let _msalInstance = null;

export function setMsalInstance(instance) {
  _msalInstance = instance;
}

function getMsalInstance() {
  if (!_msalInstance) {
    throw new Error(
      'apiClient: MSAL instance not set. Call setMsalInstance(...) at app bootstrap before any API call.'
    );
  }
  return _msalInstance;
}

// ── ApiError ──────────────────────────────────────────────────
// Single error type for both transport-level and HTTP-level
// failures. Callers can branch on `isNetworkError` for "fetch
// itself failed" vs "the server responded with non-2xx".
//
// `field` is populated for 400 validation errors that include a
// `field` key in the JSON body — useful for surfacing inline
// validation errors next to specific form inputs.
//
// `detail` is the optional human-readable detail string from the
// API's error body (when present).
//
// `hint` is the optional operator-pointer that accompanies `detail`
// on some errors (e.g. the shared-calendar consent 409 points at the
// setup runbook). translateError appends it after the detail — a
// detail says WHAT is wrong; the hint says where to go and fix it.
export class ApiError extends Error {
  constructor({ message, status, field = null, detail = null, hint = null, isNetworkError = false }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.field = field;
    this.detail = detail;
    this.hint = hint;
    this.isNetworkError = isNetworkError;
  }
}

// ── Token acquisition ────────────────────────────────────────

async function getAccessToken({ forceRefresh = false } = {}) {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) {
    throw new ApiError({
      message: 'Not signed in. Sign in first, then retry.',
      status: 401,
    });
  }
  const account = accounts[0];
  try {
    const result = await msal.acquireTokenSilent({
      ...apiRequest,
      account,
      forceRefresh,
    });
    return result.accessToken;
  } catch (err) {
    // Interactive fallback covers: first call after sign-in when the
    // API scope hasn't been consented yet, or admin policy changes
    // requiring re-consent. msal-browser raises distinct error codes
    // but they all subclass InteractionRequiredAuthError.
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msal.acquireTokenPopup({
        ...apiRequest,
        account,
      });
      return result.accessToken;
    }
    throw err;
  }
}

// ── Internals ────────────────────────────────────────────────

function assertPath(path) {
  if (typeof path !== 'string' || !path.startsWith('/')) {
    throw new Error(
      `apiClient: path must be a string starting with "/", got: ${JSON.stringify(path)}`
    );
  }
}

// Build the query string for apiGet. Skips null/undefined/''
// values; coerces booleans to 'true'/'false'; lets URLSearchParams
// handle key+value URL-encoding. Returns '' (no leading '?') if
// no params survive filtering.
function buildQueryString(params) {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    const str = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
    usp.append(key, str);
  }
  const qs = usp.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

function buildHeaders(token, method) {
  const h = {
    'authorization': `Bearer ${token}`,
    'accept':        'application/json',
  };
  if (method !== 'GET') {
    h['content-type'] = 'application/json';
  }
  return h;
}

// Single network round trip — no auth handling here, callers
// orchestrate the acquire-token / 401-retry loop.
async function doFetch(url, method, body, token) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders(token, method),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (networkErr) {
    console.error(`API network error → ${method} ${url}`, networkErr);
    throw new ApiError({
      message: 'Network error — could not reach the API.',
      status: 0,
      isNetworkError: true,
    });
  }

  const bodyText = await response.text();
  let parsed = null;
  if (bodyText.length > 0) {
    try {
      parsed = JSON.parse(bodyText);
    } catch (parseErr) {
      const apiErr = new ApiError({
        message: `Server returned ${response.status} with non-JSON response`,
        status: response.status,
      });
      console.error(`API parse error → ${method} ${url}`, apiErr);
      throw apiErr;
    }
  }

  if (response.ok) {
    return { ok: true, parsed };
  }
  return { ok: false, status: response.status, parsed };
}

// Wraps doFetch with the acquire-token, attempt-and-401-retry,
// throw-on-failure loop. Returns the parsed success body or
// throws ApiError for any non-2xx (after the retry attempt).
async function doRequest(url, method, body, options = {}) {
  // First attempt — fresh-from-cache token.
  let token = await getAccessToken();
  let result = await doFetch(url, method, body, token);

  // 401 retry: force-refresh the token and try once more. Covers
  // a token revoked mid-session or server-side scope changes that
  // require a fresh acquisition.
  if (!result.ok && result.status === 401) {
    token = await getAccessToken({ forceRefresh: true });
    result = await doFetch(url, method, body, token);
  }

  if (result.ok) {
    return result.parsed; // null for empty body, parsed object otherwise
  }

  const apiErr = new ApiError({
    message: (result.parsed && result.parsed.error)  || `HTTP ${result.status}`,
    status:  result.status,
    field:   (result.parsed && result.parsed.field)  || null,
    detail:  (result.parsed && result.parsed.detail) || null,
    hint:    (result.parsed && result.parsed.hint)   || null,
  });
  // Suppress the console.error for statuses the caller explicitly expects
  // (e.g. getTenantIntegration's 404 = the "not configured" state, which is
  // routine on-mount for an unconfigured client, not an error). The ApiError
  // is still thrown — only the noisy log is skipped.
  const expected = Array.isArray(options.expectedStatuses) && options.expectedStatuses.includes(result.status);
  if (!expected) console.error(`API error → ${method} ${url}`, apiErr);
  throw apiErr;
}

// ── Public surface ───────────────────────────────────────────

// Binary GET (PDF downloads etc.) — same auth + 401-retry loop as
// doRequest, but returns the response Blob instead of JSON-parsing
// (doFetch text-parses everything, which would mangle PDF bytes).
// First consumer: the billing snapshot PDF (2026-08-21).
export async function apiGetBlob(path, queryParams = {}) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}${buildQueryString(queryParams)}`;

  const attempt = async (token) => {
    try {
      return await fetch(url, { headers: { authorization: `Bearer ${token}` } });
    } catch (networkErr) {
      console.error(`API network error → GET ${url}`, networkErr);
      throw new ApiError({ message: 'Network error — could not reach the API.', status: 0, isNetworkError: true });
    }
  };

  let token = await getAccessToken();
  let response = await attempt(token);
  if (response.status === 401) {
    token = await getAccessToken({ forceRefresh: true });
    response = await attempt(token);
  }
  if (!response.ok) {
    // Error bodies are JSON even on binary endpoints — surface the code.
    let parsed = null;
    try { parsed = JSON.parse(await response.text()); } catch { /* non-JSON error body */ }
    const apiErr = new ApiError({
      message: (parsed && parsed.error) || `HTTP ${response.status}`,
      status: response.status,
      detail: (parsed && parsed.detail) || null,
    });
    console.error(`API error → GET ${url}`, apiErr);
    throw apiErr;
  }
  return response.blob();
}

export async function apiGet(path, queryParams = {}, options = {}) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}${buildQueryString(queryParams)}`;
  return doRequest(url, 'GET', undefined, options);
}

export async function apiPost(path, body = {}) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}`;
  return doRequest(url, 'POST', body);
}

export async function apiPatch(path, body = {}) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}`;
  return doRequest(url, 'PATCH', body);
}

export async function apiPut(path, body = {}) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}`;
  return doRequest(url, 'PUT', body);
}

// DELETE may carry a body or not — backends differ on whether they
// read it. mgmt/admin/users/{id}/entitlements/{moduleKey} doesn't
// need one. body defaults to undefined so doFetch skips the body
// rather than sending `{}`.
export async function apiDelete(path, body) {
  assertPath(path);
  const url = `${API_BASE_URL}${path}`;
  return doRequest(url, 'DELETE', body);
}
